/**
 * Session management (SPEC.md §2.2, §11 item 16) — every login, browser cookie or
 * otherwise, that's ever authenticated this account.
 *
 * `TokensCard` already lists and revokes tokens; sessions hadn't gotten the same treatment,
 * which meant no way to see or kill a login left on another device once you'd revoked the
 * one token you happened to notice. Own-account scoped, like tokens — not admin-only.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError, LOGGED_OUT_EVENT, api, clearToken, type SessionInfo } from "../api";
import { EyeToggle } from "./EyeToggle";

function when(epoch: number | null): string {
  if (!epoch) return "never";
  return new Date(epoch * 1000).toLocaleString();
}

export function SessionsCard() {
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);

  const load = useCallback(async (includeRevoked: boolean) => {
    try {
      setSessions((await api.sessions(includeRevoked)).sessions);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
      setError(err instanceof Error ? err.message : String(err));
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    void load(showRevoked);
  }, [load, showRevoked]);

  async function revoke(session: SessionInfo) {
    const message = session.current
      ? "This is the session you're using right now - revoking it logs you out. Continue?"
      : "Revoke this session? That device will be logged out immediately.";
    if (!window.confirm(message)) return;
    try {
      const { revoked_at } = await api.revokeSession(session.id);
      // Patched in place, not re-fetched: a fresh list would already exclude the row we
      // just revoked (gitea #49, D203), and it's meant to stay crossed out until an actual
      // page refresh, not vanish the instant you act on it.
      setSessions(
        (prev) => prev?.map((s) => (s.id === session.id ? { ...s, revoked_at } : s)) ?? prev,
      );
      setError(null);
      if (session.current) {
        // The confirm above promises this logs you out — without this, nothing tells the
        // app that's true. The cookie is dead server-side, but `me` stays set in memory and
        // every cached read keeps answering locally until some unrelated request happens to
        // hit the now-revoked session and 401s. Same event api.logout() fires, and the same
        // reason it also drops any stored bearer token: this is meant to behave exactly like
        // clicking Logout, not wait for the rest of the app to notice on its own.
        clearToken();
        window.dispatchEvent(new Event(LOGGED_OUT_EVENT));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <section className="panel">
      <header>
        <h2>Sessions</h2>
        <span className="row">
          <span className="muted small">Browser logins - separate from the tokens below</span>
          <EyeToggle
            open={showRevoked}
            onToggle={() => setShowRevoked((v) => !v)}
            label="revoked sessions"
          />
        </span>
      </header>

      {error && <p className="error">{error}</p>}
      {sessions === null ? (
        <p className="muted">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="muted small">No sessions.</p>
      ) : (
        <table className="tokens">
          <thead>
            <tr>
              <th>Started</th>
              <th>Last used</th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => (
              <tr key={session.id} className={session.revoked_at ? "revoked" : undefined}>
                <td className="small muted">{when(session.created_at)}</td>
                <td className="small muted">{when(session.last_used_at)}</td>
                <td>{session.current && <span className="badge">This session</span>}</td>
                <td>
                  {!session.revoked_at && (
                    <button className="btn ghost small" onClick={() => void revoke(session)}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
